import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import BookingDialog from "./BookingDialog";
import NoticeRotator from "./NoticeRotator";
import ConfettiBurst from "./ConfettiBurst";
import HeaderTrain from "./HeaderTrain";
import { Camera, ChevronDown, Facebook, Instagram, Youtube } from "lucide-react";
import { REGISTER_URL } from "./RegistrationPopup";
import { useLang } from "../../lib/i18n";

const SHOP_URL = "https://shop.loveexclusivephotography.co.uk/login";
const LOAD_DURATION = 2200;

const LANGS = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "ar", label: "العربية", flag: "🇦🇪" },
];

const SOCIALS = [
  { href: "https://www.facebook.com/LoveExclusiveUK/",             label: "Facebook",  Icon: Facebook,  color: "#1877F2" },
  { href: "https://www.instagram.com/loveexclusiveuk/",            label: "Instagram", Icon: Instagram, color: "#E1306C" },
  { href: "https://www.youtube.com/@loveexclusivephotography3538", label: "YouTube",   Icon: Youtube,   color: "#FF0000" },
];

function RedirectOverlay({ region, onDone }) {
  const { t } = useLang();
  const [pct, setPct] = useState(0);

  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t2 = Math.min(1, (now - start) / LOAD_DURATION);
      const eased = 1 - Math.pow(1 - t2, 2.2);
      setPct(Math.round(eased * 100));
      if (t2 < 1) raf = requestAnimationFrame(tick);
      else setTimeout(onDone, 180);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onDone]);

  return (
    <motion.div
      data-testid="order-interstitial"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center px-6"
    >
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="text-[90px] sm:text-[110px] leading-none"
        aria-hidden
      >
        {region === "uk" ? "🇬🇧" : "🇦🇪"}
      </motion.div>
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.5 }}
        dir="auto"
        className="mt-8 font-display font-black uppercase tracking-tight text-2xl sm:text-3xl text-black text-center"
      >
        {region === "uk" ? t("redirect.toUK") : t("redirect.toUAE")}
      </motion.p>
      <div className="mt-10 w-72 sm:w-96">
        <div className="relative h-[2px] w-full overflow-hidden bg-black/10">
          <div
            className="absolute inset-y-0 left-0 bg-black"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-3 flex items-center justify-between font-body text-[10px] uppercase tracking-[0.3em] text-black/60">
          <span data-testid="order-loading-percent">{pct}%</span>
          <span dir="auto">{t("redirect.loading")}</span>
        </div>
      </div>
      <p className="mt-8 font-body text-[10px] uppercase tracking-[0.3em] text-black/40">
        Love Exclusive Photography
      </p>
    </motion.div>
  );
}

function LanguageCircle() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("lex:lang-dropdown", { detail: { open } }));
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="header-lang-circle"
        aria-label={t("header.language")}
        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white border border-black text-[15px] leading-none hover:scale-105 transition-transform"
      >
        <span aria-hidden>{current.flag}</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="absolute right-0 top-full min-w-[140px] bg-white border border-black border-t-0 z-[60]"
            data-testid="header-lang-menu"
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => { setLang(l.code); setOpen(false); }}
                data-testid={`header-lang-option-${l.code}`}
                dir="auto"
                className={`w-full flex items-center gap-2 px-3 py-2.5 text-[11px] uppercase tracking-[0.18em] font-bold text-left hover:bg-black hover:text-white transition-colors ${
                  lang === l.code ? "bg-black/5" : ""
                }`}
              >
                <span aria-hidden>{l.flag}</span>
                <span className="flex-1">{t(`lang.${l.code}`)}</span>
                {lang === l.code && <span aria-hidden>✓</span>}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function OrderPhotosDropdown({ onOrder }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("lex:lang-dropdown", { detail: { open } }));
  }, [open]);

  const pick = (region) => () => {
    setOpen(false);
    onOrder(region);
  };

  return (
    <div className="relative" ref={ref}>
      {/* Subtle attention wiggle — a tiny rotation + lift pulse every ~5s.
          Uses transform only, GPU-friendly. Freezes while the dropdown is open. */}
      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="header-order-photos-btn"
        dir="auto"
        animate={
          open
            ? { rotate: 0, y: 0 }
            : { rotate: [0, -2, 2, -1.5, 0, 0], y: [0, -2, 0, -1, 0, 0] }
        }
        transition={{
          duration: 0.9,
          repeat: open ? 0 : Infinity,
          repeatDelay: 4.2,
          ease: "easeInOut",
          times: [0, 0.15, 0.3, 0.45, 0.6, 1],
        }}
        className="inline-flex items-center gap-2 bg-white text-black px-3 sm:px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold border border-black hover:bg-black hover:text-white transition-colors duration-300 ease-in-out"
        style={{ transformOrigin: "50% 100%", willChange: "transform" }}
      >
        <span>{t("header.orderPhotos")}</span>
        <ChevronDown size={12} strokeWidth={2.5} />
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scaleY: 0.7 }}
            animate={{ opacity: 1, scaleY: 1 }}
            exit={{ opacity: 0, scaleY: 0.7 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="absolute left-0 right-0 top-full w-full bg-white border border-black border-t-0 z-[60]"
            data-testid="header-order-menu"
            style={{ transformOrigin: "top" }}
          >
            <button
              type="button"
              onClick={pick("uk")}
              data-testid="header-order-option-uk"
              className="w-full flex items-center gap-2 px-3 py-2.5 text-[11px] uppercase tracking-[0.18em] font-bold text-left hover:bg-black hover:text-white transition-colors"
            >
              <span aria-hidden>🇬🇧</span>
              <span>UK</span>
            </button>
            <button
              type="button"
              onClick={pick("uae")}
              data-testid="header-order-option-uae"
              className="w-full flex items-center gap-2 px-3 py-2.5 text-[11px] uppercase tracking-[0.18em] font-bold text-left hover:bg-black hover:text-white transition-colors border-t border-black/10"
            >
              <span aria-hidden>🇦🇪</span>
              <span>UAE</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function RegisterLink({ testId, compact }) {
  return (
    <a
      href={REGISTER_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 bg-black text-white ${compact ? "rounded-full px-3.5 py-1.5 text-[9px]" : "px-3 sm:px-4 py-2.5 text-[10px]"} uppercase tracking-[0.2em] font-bold border border-black hover:bg-white hover:text-black transition-colors duration-300 whitespace-nowrap`}
    >
      <Camera size={12} strokeWidth={2.5} />
      <span>Register for Photo Day</span>
    </a>
  );
}

export default function Header() {
  const { t } = useLang();
  const [bookOpen, setBookOpen] = useState(false);
  const [redirectRegion, setRedirectRegion] = useState(null);
  const [confettiRegion, setConfettiRegion] = useState(null);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50 bg-white"
      >
        {/* ===== MOBILE LAYOUT (< md) — stacked & centred =====
            Logo → tagline → ORDER PHOTOS → notice carousel → toy train.
            Circular language button pinned top-right. */}
        <div
          data-testid="header-mobile"
          className="md:hidden relative px-3 pt-2.5 pb-1.5 flex flex-col items-center gap-1.5"
        >
          {/* Language circle — top-right */}
          <div className="absolute top-2 right-3">
            <LanguageCircle />
          </div>

          {/* Logo centered */}
          <a
            href="#home"
            data-testid="header-logo-mobile"
            className="flex items-center justify-center"
            aria-label="Love Exclusive Photography"
          >
            <img
              src="/brand/logo.png"
              alt="Love Exclusive"
              className="h-9 w-auto object-contain select-none"
              draggable={false}
            />
          </a>

          {/* Tagline centered */}
          <p
            data-testid="header-tagline-mobile"
            dir="auto"
            className="font-body text-[7px] font-semibold text-black/70 uppercase text-center px-6 max-w-[92vw] truncate"
            style={{ letterSpacing: "0.22em", lineHeight: 1.4 }}
          >
            {t("header.tagline")}
          </p>

          {/* Order Photos button centered */}
          <div className="flex justify-center">
            <OrderPhotosDropdown onOrder={(region) => setConfettiRegion(region)} />
          </div>
          <RegisterLink testId="header-register-link-mobile" compact />

          {/* Notice carousel — centered, readable */}
          <div className="w-full flex items-center justify-center min-h-[18px] mt-0.5">
            <NoticeRotator />
          </div>

          {/* Toy train — own lane, small, centered */}
          <div
            data-testid="header-train-row-mobile"
            className="relative h-[16px] w-full overflow-hidden"
          >
            <HeaderTrain />
          </div>
        </div>

        {/* ===== DESKTOP / TABLET-LANDSCAPE LAYOUT (≥ md) — unchanged ===== */}
        <div className="hidden md:flex max-w-[1500px] mx-auto px-3 sm:px-8 py-2 sm:py-3 flex-wrap items-center justify-between gap-x-2 sm:gap-x-4 gap-y-1.5">

          {/* LEFT — controls (ORDER PHOTOS, socials, language) */}
          <div className="order-1 flex items-center gap-1.5 sm:gap-3 shrink-0">
            <OrderPhotosDropdown onOrder={(region) => setConfettiRegion(region)} />
            <RegisterLink testId="header-register-link" />
            <div className="hidden sm:flex items-center gap-2.5" data-testid="header-socials">
              {SOCIALS.map(({ href, label, Icon, color }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={label}
                  data-testid={`header-social-${label.toLowerCase()}`}
                  className="inline-flex items-center justify-center transition-transform hover:scale-110"
                  style={{ color }}
                >
                  <Icon size={18} strokeWidth={2} />
                </a>
              ))}
            </div>
            <LanguageCircle />
          </div>

          {/* RIGHT — logo above tagline, right-aligned block */}
          <div className="order-2 md:order-3 flex flex-col items-end gap-0.5 shrink-0">
            <a
              href="#home"
              data-testid="header-logo"
              className="flex items-center"
              aria-label="Love Exclusive Photography"
            >
              <img
                src="/brand/logo.png"
                alt="Love Exclusive"
                className="h-9 sm:h-11 w-auto object-contain select-none"
                draggable={false}
              />
            </a>
            <p
              data-testid="header-tagline"
              dir="auto"
              className="font-body text-[7px] sm:text-[8px] font-semibold text-black/70 uppercase whitespace-nowrap text-right"
              style={{ letterSpacing: "0.22em", lineHeight: 1.4 }}
            >
              {t("header.tagline")}
            </p>
          </div>

          {/* CENTRE — 2-row lane: notice carousel on top, toy train below. */}
          <div
            data-testid="header-carousel-lane"
            className="order-3 md:order-2 w-full md:w-auto md:flex-1 md:min-w-0 self-stretch flex flex-col justify-center"
          >
            <div className="flex items-center min-h-[18px] sm:min-h-[22px]">
              <NoticeRotator />
            </div>
            <div
              data-testid="header-train-row"
              className="relative h-[18px] sm:h-[22px] mt-0.5 overflow-hidden"
            >
              <HeaderTrain />
            </div>
          </div>

        </div>
      </header>

      <AnimatePresence>
        {confettiRegion && (
          <ConfettiBurst
            region={confettiRegion}
            onDone={() => {
              setRedirectRegion(confettiRegion);
              setConfettiRegion(null);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {redirectRegion && (
          <RedirectOverlay
            region={redirectRegion}
            onDone={() => {
              window.location.href = SHOP_URL;
            }}
          />
        )}
      </AnimatePresence>

      <BookingDialog open={bookOpen} onOpenChange={setBookOpen} mode="order" />
    </>
  );
}
